import React, { useState, useEffect } from "react";
import {
  Button,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  FormControl,
  Box,
  Autocomplete,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import DeleteIcon from "@mui/icons-material/Delete";
import { GridColDef } from "@mui/x-data-grid";
import MUITable from "./MUITable";
import {
  getProjectName,
  processQa,
  deleteQaRecords,
  getQaAutomationData,
  previewKnowledgeAsset,
  downloadAsset,
} from "../services/BackendServices";
import Chatbot from "../components/ChatBot/ChatBot";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import { HTMLComponent } from "../components/HTMLComponent";
import { useDispatch } from "react-redux";
import { useAppSelector } from "../redux/Hook";
import "./Styles.css";
import {
  ASSET_DOWNLOAD_FAILED,
  DELETE,
  DELETE_QA_DATA,
  ERROR_PREVIEW_ASSET,
  ERROR_PROCESS_QA,
  FAILED_QA_AUTOMATION,
  INTERACT,
  PREVIEW,
  QA_AUTOMATION_ERROR,
  UNEXPECTED_QA_ERROR,
  UNKNOWN_ERROR,
} from "../constants/constants";

const QaAutomation = () => {
  const [open, setOpen] = useState(false);
  const [projectName, setProjectName] = useState<string | null>(null);
  const [projectOptions, setProjectOptions] = useState<string[]>([]);
  const [fileMap, setFileMap] = useState<{ qlik?: File; table?: File }>({});
  const [uploads, setUploads] = useState<
    Array<{
      id: number;
      asset_id: string;
      project_name: string;
      creation_date: string;
      created_by: string;
    }>
  >([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [openChat, setOpenChat] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<{ asset_id: string }>({
    asset_id: "",
  });
  const [loading, setLoading] = useState(false);

  const notification = useAppSelector((state) => state.NotificationAlert);
  const dispatch = useDispatch();

  useEffect(() => {
    fetchProjectNames();
    fetchQaAutomationData();
  }, []);

  const fetchProjectNames = async () => {
    const response = await getProjectName();
    if (response?.success) {
      setProjectOptions(response?.data?.project_names);
    } else {
      setProjectOptions([]);
    }
  };

  const fetchQaAutomationData = async () => {
    try {
      const response = await getQaAutomationData();
      if (response?.success && response?.data?.mongo_validation_assets) {
        const mappedData = response?.data?.mongo_validation_assets?.map(
          (
            item: {
              asset_id: any;
              project_name: any;
              creation_date: any;
              created_by: any;
            },
            index: number
          ) => ({
            id: index + 1,
            asset_id: item?.asset_id,
            project_name: item?.project_name,
            creation_date: item?.creation_date,
            created_by: item?.created_by,
          })
        );
        setUploads(mappedData);
      } else {
        console.error(FAILED_QA_AUTOMATION, response?.message);
      }
    } catch (error) {
      console.error(QA_AUTOMATION_ERROR, error);
    }
  };

  const handleDownloadAsset = async (assetId: string) => {
    setLoading(true);
    try {
      const response = await downloadAsset(assetId);
      if (response?.success && response?.data) {
        HTMLComponent(assetId, response?.data);
      } else {
        dispatch({
          type: "SEND_ALERT",
          data: {
            ...notification,
            enable: true,
            type: "error",
            message: response?.data?.message ?? response?.message,
            duration: 3000,
          },
        });
      }
    } catch (error) {
      dispatch({
        type: "SEND_ALERT",
        data: {
          ...notification,
          enable: true,
          type: "error",
          message: ASSET_DOWNLOAD_FAILED,
          duration: 3000,
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    const uploadToDelete = uploads?.find((upload) => upload?.id === id);
    if (!uploadToDelete) return;

    const response = await deleteQaRecords(uploadToDelete?.asset_id);
    if (response?.success) {
      setUploads(uploads?.filter((upload) => upload?.id !== id));
    } else {
      console.error(DELETE_QA_DATA, response?.error);
    }
  };

  const handlePreview = async (id: number) => {
    const uploadToPreview = uploads?.find((upload) => upload?.id === id);
    if (!uploadToPreview) return;

    setLoading(true);
    try {
      const response: any = await previewKnowledgeAsset(
        uploadToPreview?.asset_id
      );
      HTMLComponent(uploadToPreview?.asset_id, response?.data);
    } catch (error) {
      console.error(ERROR_PREVIEW_ASSET, error);
    } finally {
      setLoading(false);
    }
  };

  const handleInteract = (id: number) => {
    const uploadToInteract = uploads?.find((upload) => upload?.id === id);
    if (!uploadToInteract) return;

    setSelectedAsset({ asset_id: uploadToInteract?.asset_id });
    setOpenChat(true);
  };

  const closeChatBot = () => {
    setSelectedAsset({ asset_id: "" });
    setOpenChat(false);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, id: number) => {
    setAnchorEl(event.currentTarget);
    setSelectedId(id);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedId(null);
  };

  const handleMenuItemClick = (action: string) => {
    if (selectedId !== null) {
      if (action === DELETE) {
        handleDelete(selectedId);
      } else if (action === INTERACT) {
        handleInteract(selectedId);
      } else if (action === PREVIEW) {
        handlePreview(selectedId);
      }
    }
    handleMenuClose();
  };

  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    type: string
  ) => {
    if (event?.target?.files && event?.target?.files?.length > 0) {
      setFileMap((prev) => ({
        ...prev,
        [type]: event?.target?.files![0],
      }));
    }
  };

  const handleSubmit = async () => {
    if (!projectName || !fileMap?.qlik || !fileMap?.table) {
      return;
    }

    const formData = new FormData();
    formData?.append("project_name", projectName);
    formData?.append("qlik_image", fileMap?.qlik);
    formData?.append("tableau_image", fileMap?.table);

    try {
      setLoading(true);
      const response = await processQa(formData);
      if (response?.success) {
        fetchQaAutomationData();
        setOpen(false);
      } else {
        console.error(ERROR_PROCESS_QA, response?.message || UNKNOWN_ERROR);
      }
    } catch (error) {
      console.error(UNEXPECTED_QA_ERROR, error);
    }
  };

  const columns: GridColDef[] = [
    {
      field: "asset_id",
      headerName: "Validation Asset",
      flex: 2,
      renderCell: (params) => (
        <div
          className="asset-name-cursor-blue"
          onClick={() => handleDownloadAsset(params?.row?.asset_id)}
        >
          {params?.row?.asset_id}
        </div>
      ),
    },
    { field: "project_name", headerName: "Project Name", flex: 2 },
    { field: "creation_date", headerName: "Creation Date", flex: 1 },
    { field: "created_by", headerName: "Created By", flex: 1 },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            onClick={(event) => handleMenuOpen(event, params?.row?.id)}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Paper className="bi-automation-main">
      <Box className="bi-automation-add">
        <Button
          variant="contained"
          onClick={() => setOpen(true)}
          endIcon={<AddIcon />}
        >
          Add File
        </Button>
      </Box>

      <MUITable
        rows={uploads}
        columns={columns}
        tableHeight={450}
        loading={loading}
        defaultPageSize={10}
      />

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleMenuItemClick("preview")}>
          <ListItemIcon>
            <RemoveRedEyeIcon />
          </ListItemIcon>
          <ListItemText>Preview</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleMenuItemClick("interact")}>
          <ListItemIcon>
            <SmartToyIcon />
          </ListItemIcon>
          <ListItemText>Interact</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleMenuItemClick("delete")}>
          <ListItemIcon>
            <DeleteIcon />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Upload QA Automation Data</DialogTitle>
        <DialogContent>
        {loading && <CircularProgress />} 
          <FormControl fullWidth margin="normal">
            <Autocomplete
              options={projectOptions}
              getOptionLabel={(option) => option}
              value={projectName}
              onChange={(_, newValue) => setProjectName(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Project"
                  variant="outlined"
                />
              )}
            />
          </FormControl>

          <Box className="bi-automation-upload">
            <Button
              variant="outlined"
              component="label"
              startIcon={<CloudUploadIcon />}
            >
              Upload Qlik File
              <input
                type="file"
                hidden
                onChange={(e) => handleFileChange(e, "qlik")}
              />
            </Button>
            <Typography>{fileMap?.qlik?.name}</Typography>

            <Button
              variant="outlined"
              component="label"
              startIcon={<CloudUploadIcon />}
            >
              Upload Table File
              <input
                type="file"
                hidden
                onChange={(e) => handleFileChange(e, "table")}
              />
            </Button>
            <Typography>{fileMap?.table?.name}</Typography>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handleSubmit}>
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      <div className="qa-automation-chatbot-container">
        <Chatbot
          closeChatBot={closeChatBot}
          open={openChat}
          selectedAsset={selectedAsset?.asset_id}
        />
      </div>
    </Paper>
  );
};

export default QaAutomation;